/**
 * 리드 시연 모드 — **승격 전** 예비 고객이 메인 앱에서 볼 수 있는 범위의 SSOT.
 *
 * ## 리드가 보는 건 자기 데이터다
 * 리드도 계정 발급 때 **자기 전용 매장**을 받고, 자기가 올린 정책파일이 그 매장의 정책으로
 * 적재된다(서버 `lead-policy-sync`). 그래서 아래 화면들은 리드에게도 평소와 똑같이 "내 매장
 * 정책"을 그린다 — 리드 전용 화면을 따로 만들지 않는 게 핵심이다(화면이 두 벌이 되면 정책
 * 기능을 고칠 때 한쪽을 빠뜨린다).
 *   ⚠️ 예전엔 리드에게 매장이 없어 **다른 매장(라인업) 데이터를 대신 보여주는** 우회로가 있었고,
 *   그게 남의 도매 정책표가 리드에게 노출된 사고의 원인이었다(2026-07-27). 그 우회로는 제거됐다 —
 *   "리드에게 뭘 보여주지?"의 답은 언제나 **그 리드 자신의 데이터**다.
 *
 * ## 두 겹으로 좁힌다
 * 1) 권한(RBAC) — 리드 역할은 정책만 "읽기", 나머지 그룹은 none 이다(shared/rbac.ts).
 *    서버가 이 선을 강제하므로 CRM·재고·관리자 API 는 화면을 우회해도 403 이다. 매장이 생겨도
 *    역할은 그대로 lead 라 이 선은 변하지 않는다.
 * 2) 이 목록 — 정책 그룹 안에도 정책 관제·별칭 관리·업로드 내역처럼 **우리 내부 운영 화면**이
 *    섞여 있다. 권한만으로는 그게 다 열리므로, 예비 고객에게 보여줄 화면만 여기 골라 둔다.
 *
 * 즉 권한은 "데이터 경계", 이 목록은 "보여줄 것만 고르는 편집"이다. 시연 화면을 늘리려면
 * 이 배열에 줄을 추가한다 — 단 **읽기 전용이고 우리 운영 사정이 드러나지 않는 화면**만.
 *
 * ## 승격하면 이 목록과 무관해진다
 * 승격(리드 관리 앱 → 회원 관리)은 그 계정을 정식 회원(owner)으로 올린다. 매장은 새로 만들지
 * 않고 **리드 때 쓰던 그 매장을 그대로 이어받으므로**, 시연 중 보던 정책이 끊기지 않는다.
 * role 이 lead 가 아니게 되므로 아래 게이트가 아예 안 걸리고, 평소 권한 규칙만 적용된다.
 */

/** 시연용 전체 추출 리베표 — 표준화에서 빠지는 행 없이 업체 정책표를 그대로 펼친 화면. */
export const LEAD_DEMO_MATRIX_PATH = "/policy/rebate-matrix-full";

/** 리드가 들어갈 수 있는 경로 전부. 첫 항목이 로그인 직후 착지점이 된다. */
export const LEAD_DEMO_PATHS: string[] = [
  LEAD_DEMO_MATRIX_PATH,
  // 고객 안내용 시세표 — 정책을 실제 판매가로 바꿔 주는, 시연 가치가 가장 큰 화면.
  "/policy/quote-sheet",
  // 리베 조회 — 요금제를 고르면 업체별 리베이트를 나란히 비교. 우리 강점이 바로 보인다.
  "/policy/rebate-lookup",
];

/** LNB에 그릴 시연 메뉴 — 그룹 라벨과 항목 이름(위 경로와 순서를 맞춘다). */
export const LEAD_DEMO_NAV = {
  groupLabel: "정책관리",
  items: [
    { name: "전체 리베이트 표", path: LEAD_DEMO_MATRIX_PATH },
    { name: "시세표 작성하기", path: "/policy/quote-sheet" },
    { name: "리베 조회", path: "/policy/rebate-lookup" },
  ],
};

export function isLeadDemoPath(pathname: string): boolean {
  return LEAD_DEMO_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
